import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import { DropdownButton } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import ReactTable from "@components/ReactTable";
import StatusButton from "./Button/StatusButton";
import { deleteTree } from "../services/treeCrud";
import {
  AngleDownIcon,
  EditIcon,
  TrashIcon,
  HealthIcon,
  MapMarkerAltIcon,
  FilterIconSolid,
} from "@components/icons";
import { removeProjectTree } from "@modules/Projects/services/projectCrud";
import { useBackgroundCustomerBasicInfo } from "@hooks/useBackgroundData";
import {
  filterColumnsByPreference,
  normalizeTableColumnPreferences,
  normalizeMobileTableColumnPreferences,
} from "@modules/Customer/utils/tableColumnPreferences";
import useIsMobile from "@hooks/useIsMobile";
import Header from "@components/ListHeader/ListHeader";
import { GREEN, YELLOW, RED, BLUE, WHITE } from "@helper/colors";
import { statusList } from "@helper/healthState";

function TreeIcon({ color }) {
  const svg = (
    <svg width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8" strokeWidth="0" fill={color} />
    </svg>
  );
  return svg;
}

export function treeStatusHeader(t) {
  return {
    Header: (
      <Header
        name={`${t("tree_water_stress")}`}
        infoTitle={t("tree_status")}
        infoContent={[
          <div key="green">
            <TreeIcon color={GREEN} />- {t("health_states.healthy")}:{" "}
            {t("health_states.healthy_todo")}
          </div>,
          <div key="yellow">
            <TreeIcon color={YELLOW} />- {t("health_states.drying_out")}:{" "}
            {t("health_states.drying_out_todo")}
          </div>,
          <div key="red">
            <TreeIcon color={RED} />- {t("health_states.dry")}: {t("health_states.dry_todo")}
          </div>,
          <div key="white">
            <TreeIcon color={WHITE} />- {t("health_states.frost")}: {t("health_states.frost_todo")}
          </div>,
          <div key="blue">
            <TreeIcon color={BLUE} />- {t("health_states.too_wet")}:{" "}
            {t("health_states.too_wet_todo")}
          </div>,
          <div key="no-sensor">
            <TreeIcon color="#BDBDBD" />- {t("health_states.no_sensor")}:{" "}
            {t("health_states.no_sensor_todo")}
          </div>,
          <div key="no-data">
            <TreeIcon color="#9E9E9E" />- {t("health_states.no_data")}:{" "}
            {t("health_states.no_data_todo")}
          </div>,
          <div key="maintenance">
            <TreeIcon color="#607D8B" />- {t("health_states.maintenance_needed")}:{" "}
            {t("health_states.maintenance_needed_todo")}
          </div>,
        ]}
      />
    ),
    accessor: "health_state",

    Cell: (props) => {
      const tree = props.row.original;
      if (tree.health_state == 3) {
        let subLabel, greyShade;
        if (!tree.has_active_sensor) {
          subLabel = "health_states.no_sensor";
          greyShade = "#BDBDBD";
        } else {
          const ss = Number(tree.active_sensor_state);
          if (ss === 1 || ss === 2) {
            subLabel = "health_states.maintenance_needed";
            greyShade = "#607D8B";
          } else {
            subLabel = "health_states.no_data";
            greyShade = "#9E9E9E";
          }
        }
        return (
          <StatusButton
            status={3}
            width="fit-content"
            padding="1px 10px"
            data-tut="reactour__is2"
            style={{ background: greyShade }}
          >
            {t(subLabel)}
          </StatusButton>
        );
      }
      const health_state = statusList?.filter((item) => item.key == tree.health_state);
      let treeHealth = health_state && health_state[0] && health_state[0].value;
      return (
        <>
          <StatusButton
            status={tree.health_state}
            width="fit-content"
            padding="1px 10px"
            data-tut="reactour__is2"
          >
            {treeHealth ? `${t(treeHealth.replace(/\s/g, ""))}` : ""}
          </StatusButton>
        </>
      );
    },
  };
}
export default function TreeList({
  trees,
  setIsUpdate,
  setOpenForm,
  setFormValue,
  buttons,
  onEdit,
  page,
  setPage,
  pageSize,
  setPageSize,
  search,
  setSearch,
  ordering,
  setOrdering,
  selectedStatus,
  setSelectedStatus,
  selectedAreaIds,
  setSelectedAreaIds,
  totalCount,
  hasNext,
  hasPrevious,
  isLoading,
  areaOptions: externalAreaOptions,
}) {
  const { t } = useTranslation();
  const { customerBasicInfo } = useBackgroundCustomerBasicInfo();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const tid = query.get("selectedTree");
  const { pid } = useParams();

  // Debounced search input
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1); // Reset to first page on search
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search, setSearch, setPage]);

  const { mutateAsync: treeDelete } = useMutation(deleteTree, {
    onSuccess: () => {
      queryClient.invalidateQueries("trees");
      queryClient.invalidateQueries("trees-paginated");
      queryClient.invalidateQueries(["trees", tid]);
    },
  });
  const { mutateAsync: projectTreeDelete } = useMutation(removeProjectTree, {
    onSuccess: () => {
      queryClient.invalidateQueries(["project", pid]);
      queryClient.invalidateQueries("trees-paginated");
    },
  });
  const handleDelete = (tid) => {
    const data = {
      tree_id: tid,
    };
    Swal.fire({
      html: '<p className="text-center"> ' + "Are you sure you want to delete " + "?" + "</p>",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#286CA5",
      cancelButtonColor: "#EF3600",
      cancelButtonText: "Cancel",
      confirmButtonText: "Delete",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "Your file has been deleted.", "success");
        pid ? projectTreeDelete({ data }) : treeDelete(tid);
      }
    });
  };

  const filterTreesByStatus = (status) => {
    const statusValue = String(status);
    const findIdx = selectedStatus.indexOf(statusValue);
    if (findIdx > -1) {
      let a = selectedStatus.filter((selectedValue) => selectedValue !== statusValue);
      setSelectedStatus(a);
    } else {
      setSelectedStatus((prev) => [...prev, statusValue]);
    }
    setPage(1);
  };

  const areaOptions = React.useMemo(() => {
    if (Array.isArray(externalAreaOptions) && externalAreaOptions.length > 0) {
      return externalAreaOptions;
    }
    const byId = new Map();
    (trees || []).forEach((tree) => {
      const id = String(tree.area_id || tree?.area?.id || "");
      const name = tree.area_name || tree?.area?.name || "";
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [externalAreaOptions, trees]);

  const filterTreesByArea = (areaId) => {
    const findIdx = selectedAreaIds.indexOf(areaId);
    if (findIdx > -1) {
      setSelectedAreaIds((prev) => prev.filter((id) => id !== areaId));
    } else {
      setSelectedAreaIds((prev) => [...prev, areaId]);
    }
    setPage(1);
  };

  let statusList = [
    { id: 1, label: "health_states.healthy", status: 0 },
    { id: 2, label: "health_states.drying_out", status: 1 },
    { id: 3, label: "health_states.dry", status: 2 },
    { id: 4, label: "health_states.unknown", status: 3 },
    { id: 5, label: "health_states.frost", status: 4 },
    { id: 6, label: "health_states.wet", status: 5 },
  ];

  const visibleColumnPreferences = React.useMemo(
    () => normalizeTableColumnPreferences(customerBasicInfo?.table_column_preferences).trees,
    [customerBasicInfo?.table_column_preferences]
  );

  const mobileColumnPreferences = React.useMemo(
    () =>
      normalizeMobileTableColumnPreferences(customerBasicInfo?.mobile_table_column_preferences)
        .trees,
    [customerBasicInfo?.mobile_table_column_preferences]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: `${t("tree_cadastre_no")} / ${t("tree_name")}`,
        id: "name",
        accessor: "name",
        Cell: (props) => {
          const { tree_cadastre_no, name } = props.row.original;
          return (
            <>
              <div>
                <h6 className="mb-0">{tree_cadastre_no || name}</h6>
                {tree_cadastre_no && name && <span className="text-muted small">{name}</span>}
              </div>
            </>
          );
        },
      },
      {
        Header: "Tree Species",
        id: "species_name",
        accessor: "species_name",
        Cell: (props) => {
          return (
            <>
              <div>
                <h6 className="mb-0">
                  {props.row.original.species_name || props.row.original.species_id || ""}
                </h6>
              </div>
            </>
          );
        },
      },
      {
        Header: `${t("project.name")}`,
        id: "project_name",
        accessor: "project_name",
        Cell: (props) => {
          return (
            <>
              <div>
                <h6 className="mb-0">{props.row.original.project_name || ""}</h6>
              </div>
            </>
          );
        },
      },
      {
        Header: `${t("area")}`,
        id: "area_name",
        accessor: "area_name",
        Cell: (props) => {
          return (
            <>
              <div>
                <h6 className="mb-0">{props.row.original.area_name || ""}</h6>
              </div>
            </>
          );
        },
      },
      { ...treeStatusHeader(t), id: "health_state" },
      {
        Header: `${t("actions")}`,
        id: "actions",
        accessor: "actions",
        style: { width: "100px", textAlign: "center" },
        disableSortBy: true,
        Cell: (props) => {
          return (
            <div className="text-center">
              <span title="Edit">
                <EditIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    if (typeof onEdit === "function") {
                      onEdit(props.row.original);
                      return;
                    }
                    setIsUpdate(true);
                    setOpenForm(true);
                    setFormValue(props.row.original);
                  }}
                />
              </span>
              <span title="Remove">
                <TrashIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    let tid = props.row.original.id;
                    handleDelete(tid);
                  }}
                />
              </span>
            </div>
          );
        },
      },
    ],
    [handleDelete, onEdit, setFormValue, setIsUpdate, setOpenForm, t]
  );

  const visibleColumns = React.useMemo(() => {
    const preferences = isMobile ? mobileColumnPreferences : visibleColumnPreferences;
    return filterColumnsByPreference(columns, preferences, isMobile ? [] : ["actions"]);
  }, [columns, visibleColumnPreferences, mobileColumnPreferences, isMobile]);

  const onRowClicked = (row) => {
    const { id } = row.original;
    const params = new URLSearchParams(location.search);
    params.set("selectedTree", id);
    const basePath = pid ? `/project/${pid}/list` : location.pathname;
    navigate(`${basePath}?${params.toString()}`);
  };

  const handlePageChange = React.useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handlePageSizeChange = React.useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPage(1);
    },
    [setPageSize, setPage]
  );

  const handleSortChange = React.useCallback(
    (newOrdering) => {
      setOrdering(newOrdering);
      setPage(1); // Reset to first page on sort
    },
    [setOrdering, setPage]
  );

  return (
    <>
      <ReactTable
        columns={visibleColumns}
        data={trees}
        onRowClicked={onRowClicked}
        activeId={tid}
        pagesize={pageSize}
        size={[10, 20, 30, 40, 50]}
        buttons={buttons}
        manualPagination
        pageCount={Math.ceil(totalCount / pageSize)}
        currentPage={page}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        isLoading={isLoading}
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder={t("search_tree")}
        moreFilter={
          isMobile ? (
            <DropdownButton
              title={
                <div className="d-flex gap-2 align-items-center">
                  <FilterIconSolid size="18px" />
                  {selectedStatus.length + selectedAreaIds.length > 0 && (
                    <span
                      className="badge bg-primary rounded-pill"
                      style={{ fontSize: "10px", padding: "2px 6px" }}
                    >
                      {selectedStatus.length + selectedAreaIds.length}
                    </span>
                  )}
                </div>
              }
              variant="btn-transparent"
            >
              <div className="px-3 py-2">
                <div className="fw-semibold mb-2 small text-muted">{t("status")}</div>
                {statusList.map((status) => (
                  <div key={status.id} className="d-flex align-items-center gap-2 mb-1">
                    <input
                      data-tour="first_project"
                      type="checkbox"
                      onChange={() => filterTreesByStatus(status.status)}
                      checked={selectedStatus.includes(String(status.status))}
                    />
                    <StatusButton status={status.status} width="12px" height="12px"></StatusButton>
                    <small>{t(status.label)}</small>
                  </div>
                ))}
              </div>
              <hr className="my-2" />
              <div className="px-3 py-2">
                <div className="fw-semibold mb-2 small text-muted">{t("area")}</div>
                {areaOptions.map((area) => (
                  <div key={area.id} className="d-flex align-items-center gap-2 mb-1">
                    <input
                      type="checkbox"
                      onChange={() => filterTreesByArea(area.id)}
                      checked={selectedAreaIds.includes(area.id)}
                    />
                    <small>{area.name}</small>
                  </div>
                ))}
              </div>
            </DropdownButton>
          ) : (
            <>
              <DropdownButton
                title={
                  <div className="d-flex gap-2 align-items-center">
                    <span>{t("status")}</span>
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {statusList.map((status) => (
                  <li key={status.id} className="d-flex align-items-center gap-2 px-2 py-1">
                    <input
                      data-tour="first_project"
                      type="checkbox"
                      onChange={() => filterTreesByStatus(status.status)}
                      checked={selectedStatus.includes(String(status.status))}
                    />
                    <StatusButton status={status.status} width="12px" height="12px"></StatusButton>{" "}
                    {t(status.label)}
                  </li>
                ))}
              </DropdownButton>
              <DropdownButton
                title={
                  <div className="d-flex gap-2 align-items-center">
                    <span>{t("area")}</span>
                    <AngleDownIcon />
                  </div>
                }
                variant="btn-transparent"
              >
                {areaOptions.map((area) => (
                  <li key={area.id} className="d-flex align-items-center gap-2 px-2 py-1">
                    <input
                      type="checkbox"
                      onChange={() => filterTreesByArea(area.id)}
                      checked={selectedAreaIds.includes(area.id)}
                    />
                    <span>{area.name}</span>
                  </li>
                ))}
              </DropdownButton>
            </>
          )
        }
      />
    </>
  );
}
